import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import { Seo, localBusinessLD } from "../lib/seo";
import { HERO_IMG, SERVICES, SITE, GUARANTEES, PROCESS } from "../data/site";
import { CITIES } from "../data/cities";
import { REVIEWS, POSTS, GALLERY_FALLBACK } from "../data/content";
import { Reveal, MaskedLine, SectionHead, EASE } from "../components/Reveal";
import EditorialMarquee from "../components/Marquee";
import BeforeAfter from "../components/BeforeAfter";
import { CtaBand } from "../components/Shared";

function Hero() {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "18%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.35]);

    return (
        <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero">
            <motion.div style={{ y }} className="absolute inset-[-6%] inset-x-0" aria-hidden="true">
                <img
                    src={HERO_IMG}
                    alt=""
                    className="w-full h-full object-cover"
                    fetchpriority="high"
                />
            </motion.div>
            <div className="absolute inset-0 bg-ink/55" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/60" aria-hidden="true" />
            <motion.div style={{ opacity }} className="absolute right-8 bottom-8 hidden lg:block" aria-hidden="true">
                <div className="w-24 h-24 rounded-full border border-gold/40 flex items-center justify-center rotate-12">
                    <span className="font-display italic text-gold text-sm text-center leading-tight"> 500+<br/>projets </span>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, rotate: 8, y: 24 }}
                animate={{ opacity: 1, rotate: 4, y: [0, -12, 0] }}
                transition={{
                    opacity: { duration: 1, delay: 1.2 },
                    rotate: { duration: 1, delay: 1.2 },
                    y: { duration: 7, repeat: Infinity, ease: "easeInOut" },
                }}
                className="absolute right-16 top-[20%] hidden xl:block z-10"
                data-testid="hero-logo-card"
            >
                <div className="bg-ivory rounded-2xl px-9 py-7 border border-gold/40 shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
                    <img src="/logo-solinopro.png" alt="Logo Solino Pro — S doré" className="h-56 w-auto" width="246" height="224" />
                </div>
            </motion.div>

            <div className="relative mx-auto w-full max-w-[1400px] px-5 sm:px-8 pb-20 pt-36">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-[11px] sm:text-xs uppercase tracking-[0.34em] text-gold font-semibold mb-6 flex items-center gap-3"
                    data-testid="hero-eyebrow"
                >
                    <span className="inline-block w-10 h-px bg-gold" aria-hidden="true" />
                    Artisan BTP à {SITE.city} ({SITE.dept}) — rayon 40 km
                </motion.p>

                <h1 className="text-[13vw] sm:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.98] text-ivory" data-testid="hero-title">
                    <MaskedLine delay={0.35}>Peinture, parquet</MaskedLine>
                    <MaskedLine delay={0.5}>
                        &amp; carrelage <span className="font-display italic text-gold">qui ont</span>
                    </MaskedLine>
                    <MaskedLine delay={0.65}><span className="font-display italic text-gold">de l'allure.</span></MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: EASE }}
                    className="mt-7 max-w-xl text-base sm:text-lg text-ivory/75 leading-relaxed"
                    data-testid="hero-subline"
                >
                    Solino Pro transforme vos pièces et vos façades avec une finition d'atelier.
                    Devis gratuit sous 48 h, chantiers propres, artisan local du Vexin français.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <Link
                        to="/devis"
                        className="inline-flex items-center rounded-full bg-gold text-ink font-bold px-9 py-4 hover:scale-[1.05] transition-transform duration-300"
                        data-testid="hero-quote-button"
                    >
                        Obtenir mon devis gratuit
                    </Link>
                    <Link
                        to="/galerie"
                        className="inline-flex items-center rounded-full border border-ivory/40 text-ivory px-9 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
                        data-testid="hero-gallery-button"
                    >
                        Voir nos réalisations
                    </Link>
                </motion.div>

                <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.4 }}
                    className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.2em] text-ivory/60"
                    data-testid="hero-trust"
                >
                    {GUARANTEES.map((g) => (
                        <li key={g} className="flex items-center gap-2">
                            <span className="text-gold">✦</span> {g}
                        </li>
                    ))}
                </motion.ul>
            </div>
        </section>
    );
}

function LogoShowcase() {
    return (
        <section className="relative overflow-hidden bg-ivory" data-testid="home-logo-showcase">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28 flex flex-col items-center text-center">
                <Reveal>
                    <img
                        src="/logo-solinopro.png"
                        alt="Logo Solino Pro — S doré, artisan peinture & revêtement de sol"
                        className="h-44 sm:h-60 lg:h-72 w-auto"
                        width="320"
                        height="290"
                        data-testid="showcase-logo-image"
                    />
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-10 text-[11px] uppercase tracking-[0.34em] text-[#121212]/50 font-semibold max-w-md">
                        Artisan BTP — peinture, sols &amp; finitions · Vexin français
                    </p>
                </Reveal>
                <Reveal delay={0.22}>
                    <div className="mt-6 w-40 gold-rule" aria-hidden="true" />
                </Reveal>
            </div>
        </section>
    );
}

function ServicesBento() {
    const spans = [
        "lg:col-span-3 lg:row-span-2",
        "lg:col-span-3",
        "lg:col-span-3",
        "lg:col-span-2",
        "lg:col-span-2",
        "lg:col-span-2",
    ];
    return (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-32" data-testid="services-section">
            <SectionHead
                eyebrow="Rénovation intérieure — sols et murs"
                title="Six savoir-faire,"
                serif="une seule exigence."
                text="Peinture, sols et finitions : la rénovation intérieure de A à Z, chaque métier travaillé avec la même préparation rigoureuse. Cliquez pour découvrir nos tarifs indicatifs et nos exemples de chantiers."
            />
            <div className="mt-14 grid gap-4 lg:grid-cols-6 lg:auto-rows-[220px]">
                {SERVICES.map((s, i) => (
                    <Reveal key={s.slug} delay={i * 0.06} className={spans[i]}>
                        <Link
                            to={`/${s.slug}`}
                            className="group relative block h-full min-h-[220px] overflow-hidden border border-linew bg-coal"
                            data-testid={`service-card-${s.slug}`}
                        >
                            <img
                                src={s.hero}
                                alt={s.title}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-[1.06] transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-transparent" />
                            <div className="relative h-full flex flex-col justify-end p-6">
                                <span className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">
                                    {String(i + 1).padStart(2, "0")} — {s.price}
                                </span>
                                <h3 className="text-2xl font-light text-ivory tracking-tight group-hover:text-gold transition-colors duration-300">
                                    {s.title}
                                </h3>
                            </div>
                            <span className="absolute top-5 right-5 w-9 h-9 rounded-full border border-gold/50 text-gold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                                →
                            </span>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function Spotlight() {
    const g = GALLERY_FALLBACK[0];
    return (
        <section className="border-y border-linew bg-coal/60" data-testid="spotlight-section">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <BeforeAfter before={g.before} after={g.after} title={g.title} location={g.location} />
                </Reveal>
                <div>
                    <SectionHead
                        eyebrow="Avant / Après"
                        title="Le drag du curseur"
                        serif="dit tout ce qu'un discours tait."
                        text="Chaque chantier est documenté : état initial, travaux, résultat final. Ce n'est pas du filtre ou du hasard — c'est de la préparation, du temps et du geste maîtrisé."
                    />
                    <Reveal delay={0.2}>
                        <Link
                            to="/galerie"
                            className="mt-9 inline-flex items-center gap-3 text-gold font-semibold text-sm uppercase tracking-[0.2em] hover:gap-5 transition-all duration-300"
                            data-testid="spotlight-gallery-link"
                        >
                            Explorer la galerie complète →
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function ZonesStrip() {
    return (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-32" data-testid="zones-section">
            <SectionHead
                eyebrow="Zones d'intervention"
                title="Du Vexin à Beauvais,"
                serif="nous sommes vos voisins."
                text="Basés à Chaumont-en-Vexin, nous intervenons dans un rayon de 40 km : pour votre rénovation intérieure comme pour un simple rafraîchissement, la proximité fait des devis plus rapides et des chantiers mieux suivis."
            />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-linew border border-linew">
                {CITIES.map((c, i) => (
                    <Reveal key={c.slug} delay={i * 0.04}>
                        <Link
                            to={`/peintre-${c.slug}`}
                            className="group flex items-center justify-between gap-4 bg-ink hover:bg-coal transition-colors duration-300 p-6 h-full"
                            data-testid={`zone-link-${c.slug}`}
                        >
                            <span>
                                <span className="block text-lg text-ivory group-hover:text-gold transition-colors">{c.name}</span>
                                <span className="block text-xs text-smoke mt-1">
                                    {c.km === 0 ? "Notre commune" : `à ${c.km} km`} · {c.dept}
                                </span>
                            </span>
                            <span className="text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" aria-hidden="true">→</span>
                        </Link>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.1}>
                <p className="mt-8 text-sm text-smoke">
                    Votre commune n'est pas listée ? <Link to="/devis" className="text-gold underline underline-offset-4 hover:text-gold-light" data-testid="zones-other-link">Demandez : nous couvrons tout le secteur.</Link>
                </p>
            </Reveal>
        </section>
    );
}

function AvisPreview({ google = null }) {
    const hasGoogle = Boolean(google && Array.isArray(google.reviews) && google.reviews.length);
    const shown = hasGoogle ? google.reviews.slice(0, 3) : REVIEWS;
    return (
        <section className="border-y border-linew bg-coal/60" data-testid="avis-section">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-32">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                    <SectionHead
                        eyebrow={hasGoogle ? `Avis Google — ${google.userRatingCount} avis, note ${google.rating}/5` : "Ils nous ont fait confiance"}
                        title="La parole aux"
                        serif="chantiers finis."
                    />
                    {hasGoogle && (
                        <Reveal>
                            <a
                                href={google.googleMapsUri || "https://www.google.com/search?q=Solino+Pro+Chaumont-en-Vexin+avis"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 rounded-full border border-gold/60 text-gold px-8 py-4 hover:bg-gold/10 transition-colors"
                                data-testid="home-avis-google-link"
                            >
                                Voir notre fiche Google →
                            </a>
                        </Reveal>
                    )}
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-4">
                    {shown.map((r, i) => (
                        <Reveal key={hasGoogle ? `g-${i}` : r.avis_id} delay={i * 0.08}>
                            <figure className="h-full border border-linew bg-ink p-7 flex flex-col" data-testid={`home-review-${i}`}>
                                <div className="text-gold tracking-widest text-sm" aria-label={`${r.rating} étoiles`}>
                                    {"★".repeat(Math.round(r.rating || 5))}
                                </div>
                                <blockquote className="mt-4 flex-1 text-ivory/80 text-sm leading-relaxed">
                                    {r.text ? `« ${r.text} »` : "Avis laissé sur Google."}
                                </blockquote>
                                <figcaption className="mt-6 text-xs uppercase tracking-[0.2em] text-smoke">
                                    {r.name || r.author}{!hasGoogle && ` — ${r.city}`}
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-8 text-xs text-smoke">
                        {hasGoogle
                            ? "Avis fournis par Google — affichés par ordre de pertinence. Les avis ne sont pas vérifiés par Google."
                            : "Exemples de présentation — les avis réels seront synchronisés depuis Google Business Profile."}{" "}
                        <Link to="/avis" className="text-gold hover:underline" data-testid="avis-page-link">Voir la page avis →</Link>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

function CommunityBand({ google = null }) {
    return (
        <section className="relative overflow-hidden border-y border-linew bg-coal/60" data-testid="community-section">
            <div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{ background: "radial-gradient(60% 80% at 75% 30%, rgba(212,175,55,0.12), transparent 65%)" }}
            />
            <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold mb-5">
                        Rejoignez les clients de Solino Pro
                    </p>
                    <h2 className="text-4xl sm:text-5xl font-light tracking-tighter leading-[1.05] text-ivory">
                        Artisan de confiance dans le Vexin —{" "}
                        <span className="font-display italic text-gold">40 km autour de Chaumont-en-Vexin.</span>
                    </h2>
                    <p className="mt-6 text-smoke leading-relaxed max-w-xl">
                        500+ projets réalisés, des voisins qui nous recommandent, et une
                        note Google que nous cultivons chantier après chantier. Deux clics
                        pour nous aider à grandir — ou simplement pour nous suivre.
                    </p>
                    <div className="mt-8 inline-flex items-center gap-4 border border-gold/40 bg-gold/5 px-6 py-3" data-testid="community-google-badge">
                        <span className="font-display italic text-3xl text-gold leading-none">5.0</span>
                        <span className="text-gold tracking-widest text-sm" aria-hidden="true">★★★★★</span>
                        <span className="text-xs uppercase tracking-[0.2em] text-ivory/70">Note Google — 2 avis</span>
                    </div>
                </Reveal>

                <div className="flex flex-col gap-4">
                    <Reveal delay={0.1}>
                        <a
                            href={google?.googleMapsUri || "https://www.google.com/search?q=Solino+Pro+12+Rue+Brachedal+Chaumont-en-Vexin"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block border border-gold/40 bg-gradient-to-br from-gold/15 to-transparent p-8 hover:border-gold hover:from-gold/25 transition-all duration-300"
                            data-testid="community-avis-card"
                        >
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-gold tracking-[0.3em] text-xl" aria-hidden="true">★★★★★</span>
                                <span className="text-gold text-2xl group-hover:translate-x-1 transition-transform" aria-hidden="true">→</span>
                            </div>
                            <h3 className="mt-5 text-2xl font-light text-ivory group-hover:text-gold transition-colors">
                                Vous nous avez fait travailler ?
                            </h3>
                            <p className="mt-2 text-sm text-smoke leading-relaxed">
                                Laissez-nous un avis Google : 2 minutes qui nous aident énormément
                                et rassurent vos voisins du Vexin.
                            </p>
                            <span className="mt-6 inline-flex items-center justify-center rounded-full bg-gold text-ink font-bold px-8 py-3.5 group-hover:scale-[1.03] transition-transform duration-300" data-testid="community-avis-button">
                                Laissez-nous un avis Google
                            </span>
                        </a>
                    </Reveal>

                    <Reveal delay={0.18}>
                        <a
                            href={SITE.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between gap-5 border border-linew bg-ink p-7 hover:border-gold/60 transition-colors duration-300"
                            data-testid="community-instagram-card"
                        >
                            <div className="flex items-center gap-5">
                                <span className="w-12 h-12 rounded-full border border-gold/50 text-gold flex items-center justify-center shrink-0" aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="2" width="20" height="20" rx="5" />
                                        <circle cx="12" cy="12" r="4" />
                                        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
                                    </svg>
                                </span>
                                <div>
                                    <h3 className="text-xl text-ivory group-hover:text-gold transition-colors">@solinopro</h3>
                                    <p className="text-xs text-smoke mt-1">Chantiers, avant/après et coulisses</p>
                                </div>
                            </div>
                            <span className="shrink-0 inline-flex items-center rounded-full border border-gold/60 text-gold px-6 py-3 text-sm font-semibold group-hover:bg-gold group-hover:text-ink transition-colors duration-300" data-testid="community-instagram-button">
                                Suivre sur Instagram
                            </span>
                        </a>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function ProcessSection() {
    return (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-32" data-testid="process-section">
            <SectionHead
                eyebrow="Comment ça marche"
                title="De votre appel au"
                serif="dernier coup de pinceau."
            />
            <div className="mt-16 grid md:grid-cols-3 gap-10 lg:gap-16">
                {PROCESS.map((p, i) => (
                    <Reveal key={p.step} delay={i * 0.1}>
                        <div className="relative">
                            <span className="font-display italic text-7xl text-gold/25" aria-hidden="true">{p.step}</span>
                            <h3 className="mt-4 text-xl text-ivory font-medium">{p.title}</h3>
                            <p className="mt-3 text-sm text-smoke leading-relaxed">{p.text}</p>
                            {i < 2 && <span className="hidden md:block absolute top-10 -right-8 w-6 h-px bg-gold/40" aria-hidden="true" />}
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function BlogPreview() {
    return (
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24 lg:pb-32" data-testid="blog-preview">
            <SectionHead
                eyebrow="Le journal du chantier"
                title="Nos conseils"
                serif="d'artisan."
            />
            <div className="mt-14 grid md:grid-cols-3 gap-4">
                {POSTS.map((p, i) => (
                    <Reveal key={p.slug} delay={i * 0.08}>
                        <Link to={`/blog/${p.slug}`} className="group block border border-linew bg-coal overflow-hidden h-full" data-testid={`home-blog-${p.slug}`}>
                            <div className="aspect-[16/9] overflow-hidden">
                                <img src={p.cover} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="p-6">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                                <h3 className="mt-3 text-lg font-medium text-ivory group-hover:text-gold transition-colors leading-snug">{p.title}</h3>
                            </div>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

export default function Home() {
    const [googleAvis, setGoogleAvis] = useState(null);
    useEffect(() => {
        api.get("/avis")
            .then((r) => setGoogleAvis(r.data.google || null))
            .catch(() => {});
    }, []);
    const hasGoogle = Boolean(googleAvis && Array.isArray(googleAvis.reviews) && googleAvis.reviews.length);

    return (
        <>
            <Seo
                title="Solino Pro — Rénovation intérieure à Chaumont-en-Vexin (60) : peinture, parquet & carrelage"
                description="Artisan BTP à Chaumont-en-Vexin : rénovation intérieure, sols et murs — peinture, parquet, carrelage, habillage d'escaliers. 10+ ans d'expérience, 500+ projets. Devis gratuit sous 48 h dans un rayon de 40 km — Gisors, Méru, Marines, Beauvais, Pontoise."
                path="/"
                jsonld={localBusinessLD(hasGoogle ? { ratingValue: googleAvis.rating, reviewCount: googleAvis.userRatingCount } : null)}
            />
            <Hero />
            <EditorialMarquee />
            <LogoShowcase />
            <ServicesBento />
            <Spotlight />
            <ProcessSection />
            <ZonesStrip />
            <AvisPreview google={hasGoogle ? googleAvis : null} />
            <CommunityBand google={hasGoogle ? googleAvis : null} />
            <BlogPreview />
            <CtaBand />
        </>
    );
}
